// An id for an entry that nothing derives.
//
// The derived builders — the offline convergence mechanism, and the argument
// for why a document id must be a function of the facts — are
// `core/src/ids.rs` (#214). This is the one random-id generator.
//
// ── It stays in JavaScript because a core must not have an entropy source ────
//
// It is one of three, with `clock.js`:
//
//   * `todayISO` asks the host what day it is.
//   * `money.format` asks the host's ICU what a currency looks like.
//   * `freeId` asks the host for randomness.
//
// All three are **functions of their environment** (#214): a core function
// answers the same way on any machine for the same arguments, and
// `freeId('ent')` answers differently every time it is called, by design.
// `golden/generate.js` excludes it from the corpus by name, and
// `android/…/domain/Rules.kt` from the Kotlin spine.
//
// A core `free_id(prefix, entropy)` would mean the host generating entropy and
// marshalling it in, against `crypto.randomUUID()` in one line. The id's
// *shape* has one consumer, `is_derived_id` in `core/src/reconcile.rs`, which
// already crosses.
//
// ── Why a random id is correct here and wrong everywhere else ───────────────
//
// Derived ids exist so that two offline devices performing the same operation
// converge on one document: marking Ava present on a phone and a laptop must
// produce one attendance record and one charge, not two of each.
//
// A random id is right where duplication is **meaningful**: two separate cash
// payments of $30 on the same day are two payments. That is what "entries with
// no natural derivation" means — a cash payment, a manual charge, an ad-hoc
// discount. Everything derived from another record uses a derived id from
// `core/src/ids.rs`.

export function freeId(prefix = 'ent') {
  // crypto.randomUUID is available in every browser this app targets and in
  // Node 19+; the fallback keeps unit tests running under older runtimes.
  const uuid =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}_${uuid.replace(/-/g, '').slice(0, 24)}`;
}
